import React, { useState, useContext } from 'react';
import { AuthContext } from '../../../context/AuthContext';
import { toast } from 'react-toastify';

interface ProjectFile {
    id: number;
    filepath: string;
    projectId: number;
}

interface RechnungPosition {
    id: number;
    description: string;
    pricePerItem: number;
    numberOfItems: number;
    tax: number;
    projectFileUsed?: ProjectFile | null;
}

interface Rechnung {
    id: number;
    amount: number;
    date: string;
    projectId: number;
    pdfPath?: string | null;
    project?: {
        id: number;
        name: string;
        status: string;
    };
    roundTable?: {
        id: number;
        name: string;
        number: number;
    };
    rechnungPositions?: RechnungPosition[];
}

interface RechnungenTableProps {
    rechnungen: Rechnung[];
    loading: boolean;
    projectId: string;
    onRechnungenUpdate: (rechnungen: Rechnung[]) => void;
    onAddClick: () => void;
    onViewDetails: (rechnungId: number) => void;
}

const RechnungenTable: React.FC<RechnungenTableProps> = ({
    rechnungen,
    loading,
    projectId,
    onRechnungenUpdate,
    onAddClick,
    onViewDetails
}) => {
    const authContext = useContext(AuthContext);
    
    if (!authContext) {
        throw new Error('RechnungenTable must be used within an AuthProvider');
    }

    const { token } = authContext;
    const [searchQuery, setSearchQuery] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 20;

    const formatCurrency = (amount: number): string => {
        return new Intl.NumberFormat('de-DE', {
            style: 'currency',
            currency: 'EUR',
        }).format(amount / 100); // Convert cents to euros
    };

    const formatDate = (dateString: string): string => {
        return new Date(dateString).toLocaleDateString('de-DE', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
        });
    };

    const getTotalAmount = (): number => {
        return rechnungen.reduce((sum, rechnung) => sum + rechnung.amount, 0);
    };

    const getFilteredRechnungen = (): Rechnung[] => {
        if (!searchQuery.trim()) {
            return rechnungen;
        }

        const query = searchQuery.toLowerCase();
        return rechnungen.filter(rechnung => {
            // Search in Round Table name
            if (rechnung.roundTable?.name?.toLowerCase().includes(query)) {
                return true;
            }
            // Search in positions descriptions
            if (rechnung.rechnungPositions?.some(pos => 
                pos.description.toLowerCase().includes(query)
            )) {
                return true;
            }
            // Search in formatted date
            if (formatDate(rechnung.date).includes(query)) {
                return true;
            }
            // Search in formatted amount
            if (formatCurrency(rechnung.amount).toLowerCase().includes(query)) {
                return true;
            }
            // Search in ID
            if (rechnung.id.toString().includes(query)) {
                return true;
            }
            return false;
        });
    };

    const getPaginatedRechnungen = (): Rechnung[] => {
        const filtered = getFilteredRechnungen();
        const startIndex = (currentPage - 1) * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;
        return filtered.slice(startIndex, endIndex);
    };

    const getTotalPages = (): number => {
        const filtered = getFilteredRechnungen();
        return Math.ceil(filtered.length / itemsPerPage);
    };

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchQuery(e.target.value);
        setCurrentPage(1); // Reset to first page when searching
    };

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
    };

    const handleDeleteRechnung = async (rechnungId: number) => {
        if (!window.confirm('Möchten Sie diese Rechnung wirklich löschen?')) {
            return;
        }

        try {
            const response = await fetch(`/api/rechnungen/${rechnungId}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            });

            if (!response.ok) {
                throw new Error('Fehler beim Löschen der Rechnung');
            }

            toast.success('Rechnung erfolgreich gelöscht');
            
            // Update the list
            onRechnungenUpdate(rechnungen.filter(r => r.id !== rechnungId));
        } catch (error) {
            console.error('Error deleting Rechnung:', error);
            toast.error(error instanceof Error ? error.message : 'Fehler beim Löschen der Rechnung');
        }
    };

    return (
        <div className="rechnungen-section">
            <div className="rechnungen-header">
                <h3>Rechnungen</h3>
                <div className="rechnungen-header-actions">
                    {rechnungen.length > 0 && (
                        <div className="rechnungen-summary">
                            <span className="summary-label">Gesamt:</span>
                            <span className="summary-amount">{formatCurrency(getTotalAmount())}</span>
                        </div>
                    )}
                    <button
                        className="add-rechnung-btn"
                        onClick={onAddClick}
                    >
                        + Neue hinzufügen
                    </button>
                </div>
            </div>

            {loading ? (
                <div className="rechnungen-loading">Lade Rechnungen...</div>
            ) : rechnungen.length === 0 ? (
                <div className="no-rechnungen">
                    <p>Keine Rechnungen vorhanden</p>
                </div>
            ) : (
                <>
                    <div className="search-bar">
                        <input
                            type="text"
                            placeholder="Suche nach Round Table, Positionen, Datum, Betrag oder ID..."
                            value={searchQuery}
                            onChange={handleSearchChange}
                            className="search-input"
                        />
                        {searchQuery && (
                            <button
                                className="clear-search-btn"
                                onClick={() => setSearchQuery('')}
                                title="Suche löschen"
                            >
                                ✕
                            </button>
                        )}
                    </div>

                    {getFilteredRechnungen().length === 0 ? (
                        <div className="no-rechnungen">
                            <p>Keine passenden Rechnungen gefunden</p>
                        </div>
                    ) : (
                        <>
                            <div className="rechnungen-table-wrapper">
                                <table className="rechnungen-table">
                                    <thead>
                                        <tr>
                                            <th>Round Table</th>
                                            <th>Positionen</th>
                                            <th>Betrag</th>
                                            <th>Aktionen</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {getPaginatedRechnungen().map((rechnung) => (
                                            <tr key={rechnung.id}>
                                                <td>{rechnung.roundTable?.name || '-'}</td>
                                                <td>
                                                    {rechnung.rechnungPositions && rechnung.rechnungPositions.length > 0 ? (
                                                        <ul className="positions-list">
                                                            {rechnung.rechnungPositions.map((pos) => (
                                                                <li key={pos.id}>
                                                                    {pos.description} ({pos.numberOfItems}x {formatCurrency(pos.pricePerItem)})
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    ) : (
                                                        '-'
                                                    )}
                                                </td>
                                                <td className="amount-cell">{formatCurrency(rechnung.amount)}</td>
                                                <td className="actions-cell">
                                                    <button
                                                        className="action-btn details-btn"
                                                        onClick={() => onViewDetails(rechnung.id)}
                                                        title="Details"
                                                    >
                                                        🔍
                                                    </button>
                                                    <button
                                                        className="action-btn delete-btn"
                                                        onClick={() => handleDeleteRechnung(rechnung.id)}
                                                        title="Löschen"
                                                    >
                                                        🗑️
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                    <tfoot>
                                        <tr className="total-row">
                                            <td colSpan={2}><strong>Gesamt</strong></td>
                                            <td className="amount-cell"><strong>{formatCurrency(getTotalAmount())}</strong></td>
                                            <td></td>
                                        </tr>
                                    </tfoot>
                                </table>
                            </div>

                            {getTotalPages() > 1 && (
                                <div className="pagination">
                                    <button
                                        className="pagination-btn"
                                        onClick={() => handlePageChange(currentPage - 1)}
                                        disabled={currentPage === 1}
                                    >
                                        ← Zurück
                                    </button>
                                    
                                    <div className="pagination-info">
                                        Seite {currentPage} von {getTotalPages()} 
                                        <span className="pagination-count">
                                            ({getFilteredRechnungen().length} {getFilteredRechnungen().length === 1 ? 'Rechnung' : 'Rechnungen'})
                                        </span>
                                    </div>
                                    
                                    <button
                                        className="pagination-btn"
                                        onClick={() => handlePageChange(currentPage + 1)}
                                        disabled={currentPage === getTotalPages()}
                                    >
                                        Weiter →
                                    </button>
                                </div>
                            )}
                        </>
                    )}
                </>
            )}
        </div>
    );
};

export default RechnungenTable;
